/**
 * Phase 261i — PostHog Feature Flag hook.
 *
 * Thin React wrapper around `window.posthog.isFeatureEnabled(key)` + the
 * `onFeatureFlags` callback so components automatically re-render the
 * moment a flag's value resolves from PostHog. Used to A/B test which
 * install surface combination (banner + Get-App-CTA + profile card)
 * produces the highest install rate per persona.
 *
 * Contract:
 *   • If PostHog isn't initialized (preview pods, private mode, etc.) →
 *     returns `defaultValue` forever. Never throws.
 *   • If PostHog is initialized but the flag isn't defined / hasn't loaded
 *     yet → returns `defaultValue` until flags arrive.
 *   • If PostHog resolves the flag → returns its boolean value and
 *     re-renders consumers.
 *   • Calling `isFeatureEnabled` causes PostHog to auto-attach
 *     `$feature/<flagKey>: <variant>` to every subsequent event, so
 *     Experiments can compute conversion lift WITHOUT additional code.
 *
 * Defaults:
 *   All five Phase-261 install surfaces use `defaultValue = true` —
 *   meaning "current behaviour" is preserved when no flag is configured.
 *   Removing a flag from PostHog reverts the surface to its default state
 *   instantly. No code rollback required.
 */
import { useEffect, useState } from 'react';

export function useFeatureFlag(flagKey, defaultValue = false) {
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const ph = window.posthog;
    if (!ph) return;  // PostHog not initialised → stay on defaultValue.

    let cancelled = false;

    const read = () => {
      if (cancelled) return;
      try {
        if (typeof ph.isFeatureEnabled !== 'function') return;
        const resolved = ph.isFeatureEnabled(flagKey);
        // PostHog returns `undefined` when the flag isn't defined for
        // this user; preserve the default in that case.
        if (resolved === true || resolved === false) {
          setValue(resolved);
        }
      } catch (_) { /* analytics is best-effort */ }
    };

    // 1. Read immediately in case flags are already loaded.
    read();

    // 2. Subscribe to future flag refreshes (PostHog re-evaluates on
    //    identify(), reset(), and once the initial flag payload lands).
    let unsubscribe;
    try {
      if (typeof ph.onFeatureFlags === 'function') {
        const cb = () => read();
        ph.onFeatureFlags(cb);
        // PostHog's onFeatureFlags doesn't return an unsubscribe handle in
        // every SDK version — the cleanup is a no-op when not supported.
        unsubscribe = () => { /* PostHog SDK has no per-callback removal */ };
      }
    } catch (_) { /* ignored */ }

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [flagKey]);

  return value;
}
